import { motion } from "framer-motion";
import Header from "@/components/landing-v2/Header";
import Footer from "@/components/landing-v2/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import foxMascot from "@/assets/fox-detective.webp";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" as const },
  }),
};

interface FaqItem {
  question: string;
  answer: string;
}

const FAQ_ITEMS: FaqItem[] = [
  {
    question: "Czym jest Viraluj.pl?",
    answer:
      "Viraluj.pl to platforma AI, która pomaga twórcom internetowym generować gotowe scenariusze na Reelsy i TikToki. Analizujemy viralowe treści z Twojej branży i na ich podstawie tworzymy spersonalizowane pomysły na content.",
  },
  {
    question: "Jak działa generowanie scenariuszy?",
    answer:
      "Dodajesz źródła inspiracji (konta Instagram z Twojej branży), a nasza AI analizuje ich najlepsze posty. Na tej podstawie generujemy unikalne scenariusze dopasowane do Twojego stylu, branży i grupy docelowej.",
  },
  {
    question: "Czy muszę mieć konto na Instagramie?",
    answer:
      "Tak — podczas rejestracji podajesz swój nick na Instagramie. Dzięki temu możemy dopasować treści do Twojego profilu i śledzić Twoje statystyki wzrostu.",
  },
  {
    question: "Ile źródeł inspiracji mogę dodać?",
    answer:
      "Możesz dodać maksymalnie 5 źródeł inspiracji. To konta z Twojej branży, których viralowe treści analizujemy, by tworzyć dla Ciebie scenariusze.",
  },
  {
    question: "Jak długo trwa generowanie scenariusza?",
    answer:
      "Generowanie jednego scenariusza zajmuje od 3 do 10 minut, w zależności od wybranego trybu (podstawowy, rozszerzony z insightami Perplexity, lub custom z własnym tonem).",
  },
  {
    question: "Czy mogę edytować wygenerowane scenariusze?",
    answer:
      "Tak! Każdy scenariusz możesz edytować w naszym wbudowanym edytorze. Możesz też użyć promptera (telepromtera), który pomoże Ci nagrać materiał.",
  },
  {
    question: "Co to jest tryb rozszerzony generowania?",
    answer:
      "Tryb rozszerzony wzbogaca scenariusz o aktualne trendy, statystyki i fakty z internetu. Trwa nieco dłużej, ale daje bogatsze i bardziej angażujące treści.",
  },
  {
    question: "Ile kosztuje Viraluj.pl?",
    answer:
      "Viraluj.pl działa w modelu subskrypcyjnym. Szczegóły planów i cen znajdziesz w sekcji cennik na stronie głównej.",
  },
  {
    question: "Jak mogę usunąć swoje konto?",
    answer:
      "Usunięcie konta jest możliwe w ustawieniach aplikacji. Po usunięciu wszystkie Twoje dane, scenariusze i źródła inspiracji zostaną trwale usunięte.",
  },
  {
    question: "Mam problem techniczny — co robić?",
    answer:
      "Skorzystaj z przycisku feedbacku (dymek w prawym dolnym rogu aplikacji) lub napisz do nas na kontakt@viraluj.pl. Odpowiadamy w ciągu 24 godzin.",
  },
];

const Faq = () => {
  return (
    <div className="min-h-screen text-gray-900">
      <Header />

      <section className="relative min-h-screen pt-28 pb-20">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F9B896] via-[#FABFA3] to-[#FCCAB0]" />

        <div className="relative z-10 max-w-3xl mx-auto px-4">
          {/* Hero */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
            className="text-center mb-12"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-4">
              Najczęściej zadawane pytania
            </h1>
            <p className="text-lg text-gray-700 max-w-md mx-auto">
              Wszystko, co musisz wiedzieć o Viraluj.pl
            </p>
          </motion.div>

          {/* Accordion */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
            className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 p-6 md:p-8"
          >
            <Accordion type="single" collapsible className="space-y-2">
              {FAQ_ITEMS.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="border-b border-gray-200/60 last:border-0"
                >
                  <AccordionTrigger className="text-left font-medium text-base py-4 hover:no-underline hover:text-primary">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-sm leading-relaxed pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          {/* Fox + CTA */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
            className="flex flex-col items-center gap-4 mt-14"
          >
            <img
              src={foxMascot}
              alt="Viraluj Fox"
              className="w-36 md:w-44 object-contain"
              loading="lazy"
            />
            <p className="text-gray-700 text-sm text-center max-w-xs">
              Nie znalazłeś odpowiedzi? Napisz do nas na{" "}
              <a href="mailto:kontakt@viraluj.pl" className="text-primary font-medium hover:underline">
                kontakt@viraluj.pl
              </a>
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Faq;
